/**
 * Chart Studio → Data models panel sections, as pure data.
 * Figma: `z2UTps4F1dKNh1bbD4LtYI` node `6433:559545` ("Top") — the five
 * "Data sources" frames 559546 / 559560 / 559584 / 559596 / 559614.
 *
 * Same contract as `chartFormatSections.ts` / `artifactSectionTemplates.ts`:
 * structure and copy live here, expand/collapse state lives at the usage site
 * keyed by `id`. Never JSX, never callbacks. Renaming, reordering or adding a
 * section is an edit to this file only — no panel JSX changes.
 */

import type { FieldTypeIconKind } from "./fieldTypeIcons";

/**
 * The v2 field catalog's group keys (`FieldCatalogResponse.dates`, …). A
 * section's `id` IS the key, so the mapper reads `response[section.id]` with
 * no translation table in between.
 */
export type DataModelFieldCategory =
  | "dates"
  | "dimensions"
  | "geographic"
  | "measures"
  | "calculated";

export interface DataModelFieldSectionConfig {
  /** API `category` value this section collects. */
  id: DataModelFieldCategory;
  /** Section heading copy (e.g. "Dates"). */
  label: string;
  /** Short mark on the section's show/hide toggle ("#", "ƒ", "D", "Aa", "G"). */
  toggleLabel: string;
  /** Whether the section starts open when the panel mounts. */
  defaultExpanded: boolean;
  /**
   * Glyph (and type chip) for every field in the section. The v2 catalog
   * sends no per-field data type, so the group decides it.
   */
  fieldIcon: FieldTypeIconKind;
}

/**
 * Registry order is also the panel's section order, and the section toggles'
 * order: Measures, Calculations, Dates, Dimensions, Geographic. Every section
 * starts open.
 */
export const dataModelFieldSections = [
  { id: "measures", label: "Measures", toggleLabel: "#", defaultExpanded: true, fieldIcon: "number" },
  { id: "calculated", label: "Calculations", toggleLabel: "ƒ", defaultExpanded: true, fieldIcon: "calculated" },
  { id: "dates", label: "Dates", toggleLabel: "D", defaultExpanded: true, fieldIcon: "date" },
  { id: "dimensions", label: "Dimensions", toggleLabel: "Aa", defaultExpanded: true, fieldIcon: "text" },
  { id: "geographic", label: "Geographic", toggleLabel: "G", defaultExpanded: true, fieldIcon: "geo" },
] as const satisfies readonly DataModelFieldSectionConfig[];
